import { Plus, Search, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import type { EnterpriseDistributionSystemResponse } from 'src/features/enterprise-lead-distribution/api/use-lead-distributions'
import useLeadDistributions from 'src/features/enterprise-lead-distribution/api/use-lead-distributions'
import LeadDistributionForm from 'src/features/enterprise-lead-distribution/components/lead-distribution-form'
import LeadDistributionList from 'src/features/enterprise-lead-distribution/components/lead-distribution-list'
import DashboardModal from 'src/shared/components/ui/dashboard/dashboard-modal'
import useGetEnterprise from 'src/shared/features/enterprise/api/use-get-enterprise'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import type { EnterpriseCompanyTypeValues } from 'src/shared/schemas/forms/auth'
import { cn } from 'src/shared/utils/cn'
import { ScrollBarTailwindClassesEnterprise } from 'src/shared/utils/tailwind-styles'

import { useUploadLeadsContext } from '../../context/upload-leads-context'
import { useUploadLeadsStore } from '../../store/upload-leads-store'

export default function LeadDistributionStep() {
  const { data: userData } = useMyUserData()
  const enterpriseId = userData?.enterpriseId ?? -1
  const { data: enterprise } = useGetEnterprise({ enterpriseId })
  const companyType = enterprise?.companyType as EnterpriseCompanyTypeValues | undefined

  const { setFooterConfig, handleGoToStep } = useUploadLeadsContext()

  const formData = useUploadLeadsStore((state) => state.formData)
  const setFormData = useUploadLeadsStore((state) => state.setFormData)
  const uploadedFile = useUploadLeadsStore((state) => state.formData.uploadedFile)
  const previewData = uploadedFile?.previewData
  const businessesCount = previewData?.summary.totalRows
  const businessesCountLabel = businessesCount?.toLocaleString() ?? 0

  const [selectedDistribution, setSelectedDistribution] = useState<number | null>(
    () => formData.distributionSystemId ?? previewData?.distributionSystemId ?? null,
  )
  const [editingDistribution, setEditingDistribution] = useState<EnterpriseDistributionSystemResponse | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const { data: distributions } = useLeadDistributions({ enterpriseId })
  const trimmedSearchQuery = searchQuery.trim().toLowerCase()
  const filteredDistributions = (distributions ?? []).filter((distribution) =>
    distribution.name.toLowerCase().includes(trimmedSearchQuery),
  )
  const hasDistributions = (distributions?.length ?? 0) > 0
  const hasFilteredDistributions = filteredDistributions.length > 0
  const isFiltering = trimmedSearchQuery.length > 0
  const [view, setView] = useState<'list' | 'create' | 'edit'>('list')

  const handleCreateDistribution = () => {
    setView('create')
  }

  useEffect(() => {
    setFooterConfig({
      isPrimaryDisabled: !selectedDistribution,
      bottomLeftContent: {
        variant: 'link-button',
        text: 'Re-upload',
        onClick: () => handleGoToStep('upload'),
      },
    })
  }, [setFooterConfig, selectedDistribution])

  useEffect(() => {
    const nextLeadDistributionPreference = previewData?.leadDistributionPreference ?? null
    const currentFormData = useUploadLeadsStore.getState().formData

    if (
      currentFormData.distributionSystemId === selectedDistribution &&
      currentFormData.leadDistributionPreference === nextLeadDistributionPreference
    ) {
      return
    }

    setFormData({
      ...currentFormData,
      distributionSystemId: selectedDistribution,
      leadDistributionPreference: nextLeadDistributionPreference,
    })
  }, [previewData?.leadDistributionPreference, selectedDistribution, setFormData])

  const handleEdit = (distribution: EnterpriseDistributionSystemResponse) => {
    setEditingDistribution(distribution)
    setView('edit')
  }

  const handleDoneForm = () => {
    setView('list')
    setEditingDistribution(null)
  }

  const handleDistributionClick = (distribution: EnterpriseDistributionSystemResponse) => {
    setSelectedDistribution((prev) => (prev === distribution.id ? null : distribution.id))
  }

  return (
    <>
      <div className="flex size-full px-5">
        <div className="m-auto w-full max-w-[950px] pb-[90px]">
          <div className="sticky top-0 w-full bg-white pb-5 pt-[80px]">
            <h1 className="text-[36px] font-semibold leading-tight tracking-[-0.36px] text-primary">
              Select lead distribution
            </h1>
            <p className="mt-2 text-[18px] font-medium leading-[1.6] text-muted">
              You&apos;re about to import {businessesCountLabel} businesses
            </p>

            <div className="mt-9 flex items-center justify-between">
              <label className="flex h-[45px] w-full max-w-[350px] items-center rounded-full border border-lightBorder bg-white pl-[15px] pr-2">
                <Search className="size-4 shrink-0 text-muted" strokeWidth={1.8} />
                <input
                  type="search"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  className="ml-2 w-full bg-transparent text-[14px] font-medium leading-none text-primary placeholder:text-muted focus:outline-none [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-cancel-button]:appearance-none"
                />
                {searchQuery && (
                  <div className="flex size-[30px] shrink-0 cursor-pointer items-center justify-center rounded-full bg-transparent text-primary hover:bg-primary hover:text-white">
                    <X className="size-4 shrink-0" strokeWidth={2} onClick={() => setSearchQuery('')} />
                  </div>
                )}
              </label>

              <button
                type="button"
                className="ml-5 flex h-[45px] items-center justify-center gap-2 rounded-full bg-primary pl-5 pr-[25px] text-[14px] font-medium leading-none tracking-[-0.14px] text-white"
                onClick={handleCreateDistribution}
              >
                <Plus className="size-[14px]" strokeWidth={2.2} />
                Create distribution
              </button>
            </div>
          </div>

          <div className="flex min-h-[200px] flex-col gap-[10px] overflow-y-auto">
            {hasFilteredDistributions ? (
              <LeadDistributionList
                distributions={filteredDistributions}
                totalMembers={enterprise?.totalMembers}
                companyType={companyType}
                onEdit={handleEdit}
                onClick={handleDistributionClick}
                highlightDistributionIds={selectedDistribution ? [selectedDistribution] : undefined}
                variant="with-checkboxes"
              />
            ) : (
              <div className="flex min-h-[220px] flex-col items-center justify-center rounded-[15px] border border-dashed border-lightBorder bg-accent p-6 text-center">
                <p className="text-[18px] font-semibold leading-[1.35] tracking-[-0.18px] text-primary">
                  {hasDistributions && isFiltering ? 'No results found' : 'You have no lead distributions'}
                </p>
                <p className="mt-2 text-[15px] font-medium leading-[1.6] text-muted">
                  {hasDistributions && isFiltering
                    ? 'Try a different name or clear your search to view all distributions.'
                    : 'Create your first distribution to choose who receives imported leads.'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      <LeadDistributionModal
        view={view}
        setView={setView}
        handleDoneForm={handleDoneForm}
        editingDistribution={editingDistribution}
        enterpriseId={enterpriseId}
        companyType={companyType}
      />
    </>
  )
}

interface LeadDistributionModalProps {
  view: 'list' | 'create' | 'edit'
  setView: (view: 'list' | 'create' | 'edit') => void
  handleDoneForm: () => void
  editingDistribution?: EnterpriseDistributionSystemResponse | null
  enterpriseId: number
  companyType: EnterpriseCompanyTypeValues | undefined
}

function LeadDistributionModal({
  view,
  setView,
  handleDoneForm,
  editingDistribution,
  enterpriseId,
  companyType,
}: LeadDistributionModalProps) {
  return (
    <DashboardModal open={view === 'create' || view === 'edit'} onClose={() => setView('list')}>
      <div className={cn('overflow-hidden rounded-[25px] bg-white', ScrollBarTailwindClassesEnterprise)}>
        {view === 'create' ? (
          <LeadDistributionForm
            mode="create"
            enterpriseId={enterpriseId}
            companyType={companyType}
            onDone={handleDoneForm}
          />
        ) : (
          editingDistribution && (
            <LeadDistributionForm
              mode="edit"
              enterpriseId={enterpriseId}
              companyType={companyType}
              initial={editingDistribution}
              onDone={handleDoneForm}
            />
          )
        )}
      </div>
    </DashboardModal>
  )
}
